import { BehaviorSubject, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { Injectable, signal } from '@angular/core';
import { IAPIListResponse } from '@sondermind/utilities/models-http';
import { ILocationApiResponse, Location, Locations } from '@sondermind/utilities/models-location';
import { LocationHttpService } from '../services/location.httpservice';

@Injectable({
  providedIn: 'root'
})
export class LocationStore {
  locationList: Location[] | null = null;
  locationsLookupSubj = new BehaviorSubject<Locations>({});

  private readonly activeProviderLocationCache = signal<Location[] | null>(null);

  constructor(
    private locationService: LocationHttpService,
  ) {
  }

  // Create lookup cache from locations list
  private processLookup(locations: Location[]): Locations {
    const lookup: Locations = locations.reduce((acc: Locations, location: Location) => {
      acc[location.id.toString()] = location;
      return acc;
    }, {});
    this.locationsLookupSubj.next(lookup);
    return lookup;
  }

  // Load locations, update the cache and return processed locations
  private processLoad(response: IAPIListResponse<ILocationApiResponse>): Location[] {
    const locations = response.data.map((location) => new Location(location));
    this.locationList = locations;
    this.processLookup(locations);
    return locations;
  }

  // If lookup cache exists, return it, otherwise load from service and update the cache and return processed locations
  get locationsLookup$(): Observable<Locations> {
    if (Object.keys(this.locationsLookupSubj.value).length > 0) {
      return of(this.locationsLookupSubj.value);
    }
    return this.locationService.load({ page: 'disabled' })
      .pipe(
        map((resp) => {
          const locations = this.processLoad(resp);
          return this.processLookup(locations);
        })
      );
  }

  get locationList$(): Observable<Location[]> {
    if (this.locationList !== null) {
      return of(this.locationList);
    }
    return this.locationService.load({ page: 'disabled' })
      .pipe(
        map((resp) => this.processLoad(resp))
      );
  }

  /** Returns locations that are active for the current provider user */
  getActiveLocationList$(): Observable<Location[]> {
    if (this.activeProviderLocationCache() !== null) {
      return of(this.activeProviderLocationCache());
    }
    return this.locationService.load({
      page: 'disabled',
      filter: { key: 'active', value: 'true' }
    }).pipe(
      map((response) => {
        const locations = response.data.map((location) => new Location(location));
        this.activeProviderLocationCache.set(locations);
        return locations;
      })
    );
  }
}
